/**
 * A short ring for incoming calls, synthesized with WebAudio (no audio asset).
 * Two-tone burst (440 + 480 Hz, the North-American ring) for 1.2 s every 3 s.
 * Browsers may refuse to start audio before the user interacted with the page;
 * that is silently ignored — the banner and desktop notification still show.
 */

let ctx: AudioContext | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor() as AudioContext;
    } catch {
      return null;
    }
  }
  return ctx;
}

function burst() {
  const ac = audioContext();
  if (!ac) return;
  try {
    if (ac.state === 'suspended') void ac.resume().catch(() => {});
    const now = ac.currentTime;
    const gain = ac.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.08, now + 0.05);
    gain.gain.setValueAtTime(0.08, now + 1.15);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
    gain.connect(ac.destination);
    for (const freq of [440, 480]) {
      const osc = ac.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + 1.25);
    }
  } catch {
    /* audio blocked */
  }
}

export function isRinging(): boolean {
  return timer !== null;
}

export function startRinging() {
  if (timer) return;
  burst();
  timer = setInterval(burst, 3000);
}

export function stopRinging() {
  if (timer) clearInterval(timer);
  timer = null;
}
